/**
 * Projects — the decrypted view.
 *
 * The mirror of `workspaces.ts` for the smaller container, and it works the same
 * way for the same reason: a project root's key is generated fresh and reaches
 * each assigned member ECIES-wrapped to their public key, so being in the
 * workspace is not enough to open one.
 *
 * That is the whole point, and it is worth being precise about what it buys.
 * If a project folder's key simply chained under the workspace root — as every
 * ordinary folder's does — then "assigned to this project" would be an access
 * list the server enforces over data every workspace member already holds the
 * key to. One bug in `accessFor`, one over-broad listing, and the boundary is
 * gone. Keying it separately means an unassigned member holds nothing that
 * decrypts, whatever the server does.
 *
 * The cost, stated as plainly as the rest of the product states it: unassigning
 * someone cannot un-know the key they already had. Removal stops the server
 * serving them immediately; anything already downloaded stays readable.
 */

import { api, type ProjectWire } from './api';
import { assertValidName, decryptName, encryptName, nameHmac } from './crypto/metadata';
import { generateNodeKey } from './crypto/keys';
import { unwrapKeyFromSender, wrapKeyForRecipient } from './crypto/share';
import { cacheNodeKey } from './nodes';
import { requireVault } from './crypto/vault';
import { ensureWorkspaceKey } from './workspaces';

export interface DecryptedProject {
  id: string;
  rootNodeId: string;
  name: string;
  /** True when the name could not be read — the caller is not assigned. Shown
   *  rather than hidden: a workspace member should be able to see a project
   *  exists and ask to be added. */
  locked: boolean;
  role: string | null;
  memberCount: number;
  unread: number;
  createdAt: number;
}

/** Project id → its root node key, for this session. */
const projectKeys = new Map<string, CryptoKey>();

export const projectKey = (id: string): CryptoKey | undefined => projectKeys.get(id);

/**
 * Open a project's key from the envelope on the caller's membership row.
 *
 * Also seeds `nodes.ts`'s cache for the root node, which is what lets the file
 * browser walk into the project without a second unwrap — the same seeding
 * `enterWorkspace` does.
 */
async function adopt(p: ProjectWire): Promise<CryptoKey | null> {
  const cached = projectKeys.get(p.id);
  if (cached) return cached;
  if (!p.nodeKeyWrapped) return null;

  const vault = requireVault();
  try {
    const key = await unwrapKeyFromSender(vault.privateKey, p.nodeKeyWrapped);
    projectKeys.set(p.id, key);
    cacheNodeKey(p.rootNodeId, key);
    return key;
  } catch {
    // A key that will not open is not a crash. It means the envelope was sealed
    // to a public key this account no longer has — a vault reset, most likely —
    // and the project renders locked until someone re-wraps it.
    return null;
  }
}

export async function listProjects(workspaceId: string): Promise<DecryptedProject[]> {
  const { projects } = await api.projects.list(workspaceId);

  return Promise.all(
    projects.map(async (p) => {
      const key = await adopt(p);
      let name = 'Project';
      let locked = true;

      if (key) {
        try {
          name = await decryptName(key, p.nameEnc);
          locked = false;
        } catch {
          // Keep the placeholder. Same reasoning as `decryptNode`.
        }
      }

      return {
        id: p.id,
        rootNodeId: p.rootNodeId,
        name,
        locked,
        role: p.role,
        memberCount: p.memberCount,
        unread: p.unread,
        createdAt: p.createdAt
      };
    })
  );
}

export async function createProject(
  workspaceId: string,
  name: string
): Promise<DecryptedProject> {
  const clean = assertValidName(name);
  // Fails fast on a locked vault rather than at the wrap, where the error would
  // arrive after the name had already been typed.
  requireVault();

  const rootKey = await generateNodeKey();
  const me = await api.vault.get();

  const created = await api.projects.create(workspaceId, {
    nameEnc: await encryptName(rootKey, clean),
    // To the creator's own public key, not under the workspace root's key.
    // Every member reaches it by exactly one route, so adding the second person
    // is one more wrap rather than a change of scheme.
    nodeKeyWrapped: await wrapKeyForRecipient(me.publicKey, rootKey)
  });

  projectKeys.set(created.id, rootKey);
  cacheNodeKey(created.rootNodeId, rootKey);

  return {
    id: created.id,
    rootNodeId: created.rootNodeId,
    name: clean,
    locked: false,
    role: 'lead',
    memberCount: 1,
    unread: 0,
    createdAt: Math.floor(Date.now() / 1000)
  };
}

/**
 * Rename a project.
 *
 * Its name is encrypted under the project's own key, like everything else
 * about it — but `parent_id` is the workspace root, not the project itself, so
 * the digest that guards sibling-name uniqueness has to be computed under the
 * *workspace's* key, the same as any other child of that folder. Mirrors
 * `renameNode` in nodes.ts; a project root cannot go through that helper
 * directly because its key never comes off the parent chain.
 */
export async function renameProject(
  project: Pick<DecryptedProject, 'id' | 'rootNodeId'>,
  workspaceId: string,
  name: string
): Promise<void> {
  const clean = assertValidName(name);
  const key = projectKeys.get(project.id);
  if (!key) throw new Error('Open the project before renaming it.');

  const parentKey = await ensureWorkspaceKey(workspaceId);

  await api.nodes.update(project.rootNodeId, {
    nameEnc: await encryptName(key, clean),
    nameHmac: await nameHmac(parentKey, clean)
  });
}

/**
 * Assign somebody, wrapping the project key to them when they have a vault.
 *
 * A 404 from `publicKeyFor` is the ordinary case, not an error: the person is
 * in the workspace but has never opened Cloud. The row is written pending and
 * `completePendingProjectKeys` finishes it once they do.
 */
export async function assignMember(
  projectId: string,
  email: string,
  role: string
): Promise<{ pending: boolean }> {
  const key = projectKeys.get(projectId);
  if (!key) throw new Error('Open the project before assigning people to it.');

  let wrapped: { nodeKeyWrapped: string; fingerprint: string } | null = null;
  try {
    const recipient = await api.vault.publicKeyFor(email);
    wrapped = {
      nodeKeyWrapped: await wrapKeyForRecipient(recipient.publicKey, key),
      fingerprint: recipient.fingerprint
    };
  } catch {
    // No vault yet. Assign anyway; the sweep completes it.
  }

  const res = await api.projects.assign(projectId, { email, role, ...(wrapped ?? {}) });
  return { pending: res.pending };
}

/**
 * Finish assignments that were waiting on the assignee to make a vault.
 *
 * Runs beside `completePendingShares` and `completePendingWorkspaceKeys` on
 * unlock — all three are stuck on the same thing, a key only a member's browser
 * can wrap, so all three are finished at the same moment. Best-effort: a failure
 * retries on the next unlock.
 */
export async function completePendingProjectKeys(): Promise<void> {
  let pending;
  try {
    ({ pending } = await api.projects.pendingKeys());
  } catch {
    return;
  }
  if (!pending.length) return;

  const vault = requireVault();

  for (const row of pending) {
    try {
      // `myKeyWrapped` rides along on the response so this does not need a
      // round trip per project to fetch the key it is about to re-wrap.
      const key =
        projectKeys.get(row.projectId) ??
        (await unwrapKeyFromSender(vault.privateKey, row.myKeyWrapped));
      projectKeys.set(row.projectId, key);

      await api.projects.assign(row.projectId, {
        email: row.email,
        role: 'editor',
        nodeKeyWrapped: await wrapKeyForRecipient(row.publicKey, key),
        fingerprint: row.fingerprint
      });
    } catch {
      // Next unlock.
    }
  }
}
